import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Scale, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { displayWeight, inputToKg, getWeightUnit } from '../utils/weightUnit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeightTracker = ({ token }) => {
  const [weight, setWeight] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const system = user?.measurement_system || user?.profile?.measurement_system || 'metric';
  const unit = getWeightUnit(system);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/weight/history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.logs);
    } catch (error) {
      console.error('Error fetching weight history:', error);
    }
  };

  const logWeight = async () => {
    if (!weight || weight <= 0) return;
    const weightInKg = inputToKg(parseFloat(weight), unit);
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/weight/log?weight=${weightInKg}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchHistory();
      setWeight('');
    } catch (error) {
      console.error('Error logging weight:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = history.map(log => ({
    date: new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    weight: displayWeight(log.weight, unit)
  }));

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
      <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
        <Scale className="text-[#FF6600]" size={20} />
        {t('weight.tracker')}
      </h2>

      <div className="flex gap-2 mb-4">
        <input
          type="number"
          step="0.1"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF6600]"
          placeholder={`${t('profile.weight')} (${unit})`}
          data-testid="weight-tracker-input"
        />
        <button
          onClick={logWeight}
          disabled={loading}
          className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold text-sm flex items-center gap-1 hover:bg-[#E55C00]"
          data-testid="weight-tracker-log-btn"
        >
          <Plus size={16} />
          {t('weight.log')}
        </button>
      </div>

      {chartData.length > 0 ? (
        <div>
          <h3 className="font-semibold text-sm mb-2">{t('weight.trend')} ({unit})</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(value) => [`${value} ${unit}`, t('profile.weight')]} />
              <Line 
                type="monotone" 
                dataKey="weight" 
                stroke="#FF6600" 
                strokeWidth={2}
                dot={{ fill: '#FF6600', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('weight.noData')}</p>
        </div>
      )}
    </div>
  );
};

export default WeightTracker;
